/** left / center / right — reused by title, rating alignment, etc. */
export const ALIGN_OPTIONS = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];
/**
 * Shrine color-scheme select (`background-1`, `background-2`, `accent-1`, `accent-2`, `outline-button`,
 * `text`) — used by price color, etc. Resolve the chosen value to a CSS var via `schemeColor()`.
 */
export const SCHEME_COLOR_OPTIONS = [
    { value: 'accent-1', label: 'Accent' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'text', label: 'Text' },
    { value: 'background-1', label: 'Background' },
    { value: 'background-2', label: 'Surface' },
    { value: 'outline-button', label: 'Outline button' },
];
/**
 * Shrine "Margins" group — separate DESKTOP + MOBILE top/bottom in px (0–45, step 3). Render with
 * `marginStyle()`, which emits the desktop margins inline plus `--aqx-mmt`/`--aqx-mmb` CSS vars; the global
 * mobile rule in theme.css swaps to those under ≤749px. Mobile margins default to ~half the desktop value
 * so phones read tighter out-of-the-box (desktop keeps its spacing) — each remains fully adjustable.
 */
export function marginFields(defTop = 15, defBottom = 15) {
    const mobTop = Math.round(defTop / 2);
    const mobBottom = Math.round(defBottom / 2);
    return [
        {
            id: 'marginTop',
            type: 'range',
            label: 'Top',
            group: 'Margins',
            min: 0,
            max: 45,
            step: 3,
            default: defTop,
            unit: 'px',
        },
        {
            id: 'marginBottom',
            type: 'range',
            label: 'Bottom',
            group: 'Margins',
            min: 0,
            max: 45,
            step: 3,
            default: defBottom,
            unit: 'px',
        },
        // Mobile margins live in the "Mobile" group (short labels stay inline + a clear header, instead of long
        // "(mobile)" labels that broke the inspector row layout).
        {
            id: 'mobileMarginTop',
            type: 'range',
            label: 'Top margin',
            group: 'Mobile',
            min: 0,
            max: 45,
            step: 3,
            default: mobTop,
            unit: 'px',
        },
        {
            id: 'mobileMarginBottom',
            type: 'range',
            label: 'Bottom margin',
            group: 'Mobile',
            min: 0,
            max: 45,
            step: 3,
            default: mobBottom,
            unit: 'px',
        },
    ];
}
/**
 * Font-weight options shared by every text-bearing block's "Font weight" control. `''` = keep the block's
 * current/theme weight (so an untouched block is byte-identical — zero migration). NOTE: a heavier value only
 * renders heavier if the store's theme actually LOADS that face (font roles in theme settings); otherwise the
 * browser falls back to the nearest loaded weight. Poppins (the self-hosted default) ships the whole
 * 400→900 ladder in `packages/tokens/src/theme.css` — 900 was added on 24/09/2026 because "Black" silently
 * painted as 800 before that (the `font-coverage` guard in `packages/i18n` pins the shipped files).
 */
export const WEIGHT_OPTIONS = [
    { value: '', label: 'Default' },
    { value: '400', label: 'Normal' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'Semibold' },
    { value: '700', label: 'Bold' },
    { value: '800', label: 'Extra-bold' },
    { value: '900', label: 'Black' },
];
/** The reusable "Font weight" select. Drop into a block's `fields[]`; read with `readWeight()` in render. */
export const weightField = {
    id: 'weight',
    type: 'select',
    label: 'Font weight',
    default: '',
    options: WEIGHT_OPTIONS,
};
/**
 * The reusable "Desktop width" range (% of the parent column, 20–100, default 100 = unchanged). Drop into a
 * text-bearing block's `fields[]`; render with `widthCss()`. It only applies at ≥750px (the Shrine desktop
 * breakpoint); the phone is driven by {@link mobileWidthField}, which a block opts into separately.
 */
export const widthField = {
    id: 'desktopWidth',
    type: 'range',
    label: 'Desktop width',
    min: 20,
    max: 100,
    step: 5,
    default: 100,
    unit: '%',
};
/**
 * The phone half of the pair — same range, same "100 = unchanged" contract, read by the SAME `widthCss()`
 * (pass the block's mobile alignment as its 4th argument to turn it on). It lives in the "Mobile" group,
 * beside "Mobile alignment", because that is the group a seller opens when the phone is what is wrong.
 *
 * It exists because "narrow on desktop only" was an assumption, not a rule: a 40-character paragraph
 * centred in a phone column is exactly what a seller asks for under a hero image (founder, 22/09/2026,
 * after the same request on `icon-with-text`). Leaving it out meant retyping the copy short enough to fit.
 */
export const mobileWidthField = {
    id: 'mobileWidth',
    type: 'range',
    label: 'Mobile width',
    group: 'Mobile',
    min: 20,
    max: 100,
    step: 5,
    default: 100,
    unit: '%',
};
/**
 * THE SAME PAIR, IN PIXELS (founder, 24/09/2026).
 *
 * « le composant text dans le builder doit avoir son width configurable par les pixel et pas par taille
 * qui change variable de pourcentage ». A percentage is a share of the COLUMN, and the column is not the
 * same width in a full-bleed hero, a two-column section and the builder's phone preview — so a « #1
 * trending · 5,000+ sold this month » pill set at 40 % is a different pill on every page it lands on.
 * A number of pixels is the same box everywhere.
 *
 * Contract:
 *  - UNSET = full width, the way every block already paints (the inspector draws « Full width » in place
 *    of the handle; taking it over starts at `default`). No published page moves.
 *  - Rendered by the SAME `widthCss()`, as `width:<n>px;max-width:100%` (05/10/2026 — the box IS the
 *    number; until that day it was a `max-width`, and a short pill hugged its words instead): a 480px
 *    box on a 360px phone column still fits its column. Positioned by the device's own alignment, as
 *    before. In the two-column sections the COLUMN grows to hold the box — see `columnWidthPx()`.
 *  - On the `text` block the `%` pair above is GONE — shown nowhere, read nowhere (`legacyPercent: false`).
 *    It was first kept as a legacy row while it held a value, and the founder saw the two rows side by
 *    side the same afternoon: « remove the desktop width pourcentage, here they are contradicting the
 *    pixel and pourcentage ». A stored 60 % (the rich-text preset seeded it, 22-24/09) now paints full
 *    width until a pixel value is set. `heading` and `text-with-icon` still use the `%` pair.
 *  - `px` is a free unit (`rangeBounds.ts`): the box beside the handle takes anything up to 1000px, and
 *    the desktop track's own 1200 ceiling stands above that.
 */
export const widthPxField = {
    id: 'desktopWidthPx',
    type: 'range',
    label: 'Desktop width',
    inheritLabel: 'Full width',
    min: 100,
    max: 1200,
    step: 10,
    default: 480,
    unit: 'px',
};
/** The phone half of {@link widthPxField} — same contract, read with the block's MOBILE alignment. */
export const mobileWidthPxField = {
    id: 'mobileWidthPx',
    type: 'range',
    label: 'Mobile width',
    inheritLabel: 'Full width',
    group: 'Mobile',
    min: 80,
    max: 600,
    step: 10,
    default: 280,
    unit: 'px',
};
/**
 * The reusable "Line height" slider — the leading, as a multiple of the font size. Drop into a text-bearing
 * block's `fields[]`; read with `readLineHeight()` in render.
 *
 * ## Why it exists
 *
 * A text block's leading was the THEME's leading and nothing else: `--aqx-theme-lh-body` (1.8) for body
 * copy, `--aqx-theme-lh-heading` (1.3) for a heading. 1.8 is the right number for the 14px paragraph the
 * body role was tuned for — a 25px line box, comfortable. It is the wrong number the moment a seller pushes
 * "Text size" to 24 to use a paragraph as a statement: the same ratio gives a 43px line box, ~19px of blank
 * between lines, and the sentence falls apart at the first wrap (founder, 2026-09-20, the MOVE product page:
 * « why when the text goes back inline we have soo much space between lines »). There was no control to
 * answer that with — the only way out was retyping the sentence short enough not to wrap.
 *
 * ## Why it starts UNSET
 *
 * `themeDefault` is the leading the block already paints, and an absent value keeps painting exactly that,
 * so not one published page moves. The slider is drawn as an "Automatic" button until the seller takes it
 * over, and it takes over AT `themeDefault` — the handle starts where the text already is, so nothing jumps
 * under their hand. That is `inheritLabel` standing on its own, without an `inheritFrom` sibling; see
 * `RangeField.inheritLabel`.
 *
 * @param themeDefault the block's own theme leading — 1.8 for a body block, 1.3 for a heading.
 */
export function lineHeightField(themeDefault, autoFromSize) {
    return {
        id: 'lineHeight',
        type: 'range',
        label: 'Line height',
        info: 'Space between lines, as a multiple of the text size. Left automatic, it follows the theme.',
        min: 0.9,
        max: 2.4,
        step: 0.05,
        // A ratio has no unit to read the answer off, and 2.4 is where the HANDLE stops, not the seller: a
        // 100px word set as a poster line wants more air than a paragraph ever does (founder, 20/09/2026).
        bounds: 'free',
        default: themeDefault,
        inheritLabel: 'Automatic',
        // Quand l'automatique est une COURBE et non un nombre figé, la poignée doit démarrer sur la valeur
        // que la courbe peint à la taille du moment — sinon la reprise fait sauter le texte.
        ...(autoFromSize ? { autoFromSize } : {}),
    };
}
/** Shrine "Display on" visibility (All devices / Mobile only / Desktop only). Render with `visibilityCss()`. */
export const visibilityField = {
    id: 'visibility',
    type: 'select',
    label: 'Display on',
    group: 'Visibility',
    default: 'always-display',
    options: [
        { value: 'always-display', label: 'All devices' },
        { value: 'desktop-hidden', label: 'Mobile only' },
        { value: 'mobile-hidden', label: 'Desktop only' },
    ],
};
//# sourceMappingURL=blockFields.js.map